import React from 'react';
import { ArrowLeft, Calendar, MapPin, TrendingDown, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useLanguage } from '@/contexts/LanguageContext';

const AlibabaLiegeArticle = () => {
  const { t } = useLanguage();

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 to-pink-50">
      <div className="container mx-auto px-6 py-8">
        {/* Header */}
        <div className="mb-8">
          <Button 
            variant="ghost" 
            onClick={() => window.history.back()}
            className="mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Industry Highlights
          </Button>
          
          <div className="flex items-center gap-4 mb-4">
            <Badge className="bg-red-500">
              <TrendingDown className="h-3 w-3 mr-1" />
              {t('article.alibaba.badge')}
            </Badge>
            <div className="flex items-center text-gray-600">
              <Calendar className="h-4 w-4 mr-2" />
              {t('article.alibaba.date')}
            </div>
            <div className="flex items-center text-gray-600">
              <MapPin className="h-4 w-4 mr-2" />
              {t('article.alibaba.location')}
            </div>
          </div>
          
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            {t('article.alibaba.title')}
          </h1>
          
          <p className="text-xl text-gray-600 leading-relaxed">
            {t('article.alibaba.summary')}
          </p>
        </div>

        {/* Featured Image */}
        <div className="mb-8">
          <img 
            src="https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80" 
            alt={t('article.alibaba.imageCaption')}
            className="w-full h-96 object-cover rounded-lg shadow-lg"
          />
          <p className="text-sm text-gray-500 mt-2 text-center">
            {t('article.alibaba.imageCaption')}
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Highlight Box */}
            <Card className="border-l-4 border-l-red-500 bg-red-50">
              <CardContent className="pt-6">
                <div className="flex items-start">
                  <XCircle className="h-6 w-6 text-red-600 mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-red-800 mb-2">Key Highlight</h3>
                    <p className="text-red-700">{t('article.alibaba.highlight')}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Article Sections */}
            <div className="prose prose-lg max-w-none">
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  {t('article.alibaba.section1.title')}
                </h2>
                <p className="text-gray-700 leading-relaxed">
                  {t('article.alibaba.section1.content')}
                </p>
              </div>

              <div className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  {t('article.alibaba.section2.title')}
                </h2>
                <p className="text-gray-700 leading-relaxed">
                  {t('article.alibaba.section2.content')}
                </p>
              </div>

              <div className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  {t('article.alibaba.section3.title')}
                </h2>
                <p className="text-gray-700 leading-relaxed">
                  {t('article.alibaba.section3.content')}
                </p>
              </div>

              <div className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  {t('article.alibaba.section4.title')}
                </h2>
                <p className="text-gray-700 leading-relaxed">
                  {t('article.alibaba.section4.content')}
                </p>
              </div>
            </div>

            {/* Impact Analysis */}
            <Card className="bg-gradient-to-r from-red-50 to-pink-50 border-red-200">
              <CardHeader>
                <CardTitle className="flex items-center text-red-800">
                  <XCircle className="h-5 w-5 mr-2" />
                  {t('article.alibaba.impact.title')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-red-700">
                  {t('article.alibaba.impact.content')}
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Key Metrics */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Key Metrics</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Company</span>
                  <span className="font-semibold">Alibaba/Cainiao</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Existing Facility</span>
                  <span className="font-semibold text-green-600">30,000 m²</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Cancelled</span>
                  <span className="font-semibold text-red-600">44,000 m²</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Status</span>
                  <Badge variant="destructive">Expansion Cancelled</Badge>
                </div>
              </CardContent>
            </Card>

            {/* Source */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Source</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-600 mb-4">
                  EcommerceNews.eu
                </p>
                <Button 
                  className="w-full" 
                  onClick={() => window.open('https://ecommercenews.eu/alibaba-cancels-liege-expansion/', '_blank')}
                >
                  Read Original Article
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AlibabaLiegeArticle;